import { motion } from "framer-motion";
import { Icon } from "./Icon";

export interface TabItem {
  id: string;
  label: string;
  /** Material Symbols glyph name (same vocabulary as `StepRail`). */
  icon?: string;
}

interface TabsProps {
  tabs: TabItem[];
  active: string;
  onSelect: (id: string) => void;
  /**
   * Shared `layoutId` for the sliding underline. Override when two tab strips
   * are mounted at once, otherwise the indicator flies between them.
   */
  layoutId?: string;
  className?: string;
}

/**
 * Horizontal icon + label tab strip with a motion underline. Tabs may appear
 * and disappear at runtime (the Engagement tab) — the indicator re-parents to
 * whichever tab is active.
 */
export function Tabs({ tabs, active, onSelect, layoutId = "kit-tabs-active", className = "" }: TabsProps) {
  return (
    <nav
      role="tablist"
      // shrink-0: as a flex child in a column, the strip would otherwise be
      // squashed to nothing the moment the panel below it overflows — which is
      // exactly what happens when a tab is added at runtime.
      className={`flex shrink-0 overflow-x-auto border-b border-outline-variant/30 px-3 ${className}`}
    >
      {tabs.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(t.id)}
            className={`relative flex flex-1 shrink-0 flex-col items-center gap-1.5 px-2 pb-2.5 pt-3 font-display text-[9.5px] font-medium uppercase tracking-[0.14em] transition-colors ${
              isActive ? "text-secondary" : "text-on-surface-variant/70 hover:text-primary"
            }`}
          >
            {t.icon ? <Icon name={t.icon} size={22} fill={isActive} /> : null}
            {t.label}
            {isActive ? (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-x-0 -bottom-px h-0.5 bg-secondary"
              />
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
